import "./results.css";
import { WarningIcon, CheckCircleIcon } from "../../assets/icons";
import { useGoogleTagManager } from "@tracktor/react-google-tag-manager";

const knowMoreLink = "https://juntospelaataxia.com.br/pt-BR/";
const freeTestLink = "https://biibpsp.my.salesforce-sites.com/WebToLeadPhysician";
const folhetoTesteGeneticoPdf = "/folheto-teste-genetico.pdf";

interface ResultsProps {
  answers: Record<string, number>;
  onReset: () => void;
}

const ResultIcon = (score: number) => {
  return score <= 2 ? <CheckCircleIcon /> : <WarningIcon />;
};

const LowPossibility = () => {
  return (
    <div>
      <h2 className="results-title">
        0 a 2 pontos – Baixa suspeita para Ataxia de Friedreich
      </h2>
      <p className="results-description">
        Os achados clínicos apresentados são pouco compatíveis com o fenótipo clássico da Ataxia de Friedreich (AF), configurando um cenário de baixa suspeição diagnóstica no momento. Recomenda-se manter acompanhamento clínico a longo prazo, com atenção à possível evolução dos sintomas motores e da fala. Diante de agravamento funcional ou surgimento de novos sinais neurológicos, sugere-se a reaplicação do AF‑Screen como ferramenta de reavaliação estruturada. É prudente investigar outras possíveis etiologias para o quadro atáxico, incluindo causas inflamatórias, tóxicas, carenciais (deficiência de vitamina E ou B12) e distúrbios metabólicos. Em todos os casos, o seguimento clínico deve priorizar medidas de prevenção secundária e vigilância funcional contínua.
      </p>
    </div>
  );
};

const MediumPossibility = () => {
  return (
    <div>
      <h2 className="results-title">
        3 a 4 pontos – Suspeita moderada para Ataxia de Friedreich ou outra ataxia hereditária
      </h2>
      <p className="results-description">
        O conjunto de sinais identificados aponta para uma suspeita clínica intermediária de AF ou de outra forma de ataxia hereditária. A gravidade e o tipo dos achados justificam uma investigação mais aprofundada. Recomenda-se o encaminhamento do paciente para avaliação neurológica especializada, preferencialmente com profissional com expertise em distúrbios do movimento. Exames laboratoriais de triagem, como hemograma, dosagens vitamínicas (B12, E), função tireoidiana e imagem de sistema nervoso central (ressonância magnética de encéfalo e medula espinhal), devem ser considerados conforme disponibilidade local. A indicação de testagem genética para expansões GAA no gene FXN pode ser apropriada, especialmente em pacientes com progressão sintomática ou achados sistêmicos associados, como pé cavo, escoliose ou sinais cardiológicos. Recomenda-se ainda oferecer aconselhamento clínico ao paciente e sua família, e reforçar a importância do acompanhamento contínuo e de longo prazo.
      </p>
    </div>
  );
};

const HighPossibility = () => {
  return (
    <div>
      <h2 className="results-title">
        5 pontos ou mais – Alta suspeita para Ataxia de Friedreich
      </h2>
      <p className="results-description">
        A combinação de achados neurológicos, sistêmicos e familiares é altamente sugestiva de Ataxia de Friedreich ou de uma ataxia hereditária de padrão semelhante. Diante dessa suspeita elevada, é recomendável priorizar a solicitação de testagem genética direcionada, com foco em expansões GAA no gene FXN e, quando possível, a realização de painéis genéticos para ataxias hereditárias. O paciente deve ser encaminhado o mais breve possível para uma avaliação com um neurologista especialista e o acompanhamento deve ser complementado com suporte de uma equipe multidisciplinar, incluindo cardiologia (devido à alta prevalência de cardiomiopatia hipertrófica), fisiatria e fonoaudiologia. A investigação cardíaca inicial deve incluir, pelo menos, eletrocardiograma e ecocardiograma. Além disso, recomenda-se iniciar o aconselhamento genético com a família, esclarecendo o padrão de herança da condição (autossômica recessiva), a possibilidade de existirem familiares portadores da alteração genética ou ainda assintomáticos, bem como as implicações para o planejamento familiar futuro. Cabe ressaltar que, mesmo diante de alta pontuação, é fundamental confirmar se a condição está realmente progredindo ao longo do tempo e excluir outros diagnósticos diferenciais antes de concluir a investigação.
      </p>
    </div>
  );
};

export const Results = ({ answers, onReset }: ResultsProps) => {
  const { sendEvent } = useGoogleTagManager();
  const score = Object.values(answers).reduce((acc, answer) => acc + answer, 0);

  const onRetakeClick = () => {
    sendEvent({
      event: "gtm.formSubmit",
      formId: "biogen_mfars_calculator",
      formClasses: ["retake-button"],
      elements: [
        {
          name: "calc_action",
          type: "hidden",
          value: "retake",
        },
      ],
    });
    onReset();
  };

  const onKnowMoreClick = () => {
    sendEvent({
      event: "gtm.linkClick",
      linkData: {
        targetUrl: knowMoreLink,
        elementId: "result-know-more-button",
        elementClasses: ["learn-more-link"],
        elementTarget: "_blank",
      },
    });

    window.open(knowMoreLink, "_blank");
  };

  const onFreeTestClick = () => {
    window.open(freeTestLink, "_blank");
  };

  return (
    <div className="results-container">
      <div className="results-content">
        <h1 className="results-points">
          {ResultIcon(score)} Resultado: {score} Pontos
        </h1>

        {score <= 2 && <LowPossibility />}
        {score >= 3 && score <= 4 && <MediumPossibility />}
        {score >= 5 && <HighPossibility />}

        {score >= 3 && (
          <div className="results-cta-row">
            <button
              onClick={onFreeTestClick}
              rel="noopener noreferrer"
              className="open-test-button results-cta-button"
              data-name="biogen_free_test"
            >
              Solicite o teste genético para confirmar a Ataxia de Friedreich
            </button>
            <a
              href={folhetoTesteGeneticoPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="results-folheto-link"
            >
              Acesse o folheto explicativo sobre o teste genético
            </a>
          </div>
        )}
      </div>
      <div className="results-actions">
        <button
          className="prev-button"
          data-name="biogen_retake"
          onClick={onRetakeClick}
        >
          Refazer Teste
        </button>
        <button
          id="result-know-more-button"
          rel="noopener noreferrer"
          className="learn-more-link results-know-more-right"
          onClick={onKnowMoreClick}
        >
          Conheça o programa Juntos pela Ataxia
        </button>
      </div>
    </div>
  );
};
